import React from "react";
import { ConfigProvider } from "antd";
import { ThemesColors, FontFamily, FontSize, FontWeight, LightModeColors, DarkModeColors } from "../themes";
import { adaptFontSize } from "@utils/adaptFontSize";
import { useSelectCurrentTheme } from "@features/reducers/core/themeSelectors";

interface IAntDesignConfigProps {
    children: React.ReactNode;
}

export const AntDesignConfig: React.FC<IAntDesignConfigProps> = ({ children }) => {
    const currentTheme = useSelectCurrentTheme();
    const themeColors = currentTheme === "light" ? LightModeColors : DarkModeColors;

    const theme = {
        token: {
            // Colors
            colorPrimary: ThemesColors.mainBlueDark500,
            colorSuccess: ThemesColors.mainBlueDark500,
            colorWarning: ThemesColors.warningColor,
            colorError: ThemesColors.error500,
            colorInfo: ThemesColors.azureBlue,

            // Background colors
            colorBgContainer: themeColors.iceBlue,
            colorBgElevated: themeColors.iceBlue,
            colorBgLayout: themeColors.iceBlue,
            colorBgSpotlight: themeColors.iceBlue,

            // Text colors
            // colorText: themeColors.moduleName,
            // colorTextSecondary: themeColors.userType,
            // colorTextTertiary: themeColors.breadcrumbName,
            colorTextQuaternary: "var(--input-placeholder)",

            // Border colors
            // colorBorder: themeColors.mainSearchBarStroke,
            // colorBorderSecondary: themeColors.secondarySearchBarStroke,

            // Text colors
            colorText: ThemesColors.gray900,
            colorTextSecondary: ThemesColors.gray600,
            colorTextTertiary: ThemesColors.gray500,

            // Border colors
            colorBorder: ThemesColors.gray300,
            colorBorderSecondary: ThemesColors.gray100,

            // Font settings
            fontFamily: FontFamily.poppins,
            fontSize: adaptFontSize(FontSize.mg),
            fontSizeSM: adaptFontSize(FontSize.sm),
            fontSizeLG: adaptFontSize(FontSize.lg),
            fontSizeXL: adaptFontSize(FontSize.xl),

            // Font weight
            fontWeightStrong: FontWeight.bold,
            fontWeight: FontWeight.normal,

            // Border radius
            borderRadius: 6,
            borderRadiusLG: 8,
            borderRadiusSM: 4,

            // Spacing
            padding: 16,
            paddingLG: 24,
            paddingSM: 12,
            paddingXS: 8,

            // Control height
            // controlHeight: 40,
            // controlHeightLG: 48,
            // controlHeightSM: 32,
        },
        components: {
            Button: {
                borderRadius: 8,
                controlHeight: 32,
                controlHeightLG: 40,
                // controlHeightSM: 32,
                fontWeight: FontWeight.medium,
            },
            Input: {
                borderRadius: 8,
                controlHeight: 38,
                controlHeightLG: 40,
                inputFontSizeLG: adaptFontSize(FontSize.mg),
                controlHeightSM: 34,

                colorBgContainer: "var(--input-fill)",
                colorBgElevated: "var(--input-fill)",
                colorBgLayout: "var(--input-fill)",
                colorBgSpotlight: "var(--input-fill)",

                colorText: "var(--input-text)",
                colorTextSecondary: "var(--input-placeholder)",
            },
            Select: {
                borderRadius: 8,
                controlHeight: 40,
                // controlHeightLG: 48,
                // controlHeightSM: 32,

                colorBgContainer: "var(--input-fill)",
                colorBgElevated: "var(--input-fill)",
                colorBgLayout: "var(--input-fill)",
                colorBgSpotlight: "var(--input-fill)",

                colorText: "var(--input-text)",
                colorTextSecondary: "var(--input-placeholder)",
            },
            DatePicker: {
                borderRadius: 8,
                controlHeight: 38,
                controlHeightLG: 40,
                inputFontSizeLG: adaptFontSize(FontSize.mg),
                controlHeightSM: 34,
            },
            Steps: {
                fontSize: adaptFontSize(FontSize.mg),
                itemFontSize: adaptFontSize(FontSize.mg),
            },
            Table: {
                borderRadius: 8,
                headerBg: "var(--header-background)",
                // headerColor: themeColors.moduleName,
                rowHoverBg: "var(--interface-background)",
            },
            Modal: {
                borderRadius: 8,
                headerBg: "var(--header-background)",
                contentBg: "var(--interface-background)",
            },
            Switch: {
                colorPrimary: ThemesColors.mainBlueDark500,
                colorPrimaryHover: ThemesColors.mainBlueDark400,
            },
            Checkbox: {
                colorPrimary: ThemesColors.mainBlueDark500,
                colorPrimaryHover: ThemesColors.mainBlueDark400,
            },
        },
    };

    return <ConfigProvider theme={theme}>{children}</ConfigProvider>;
};
